import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type Rack, type Sector } from '../api';
import { useApi } from '../lib/useApi';
import { Cargando, Encabezado, Icono, Numero, PanelError, useConfirmar } from '../components/ui';
import {
  areaDeDibujo,
  codigoEntra,
  codigosDeUbicacion,
  etiquetaDePosicion,
  etiquetasElegidas,
  orientacionNatural,
  svgEtiquetaUbicacion,
  type EtiquetaUbicacion,
  type Orientacion,
  type SeleccionRack,
} from '../lib/etiquetasUbicacion';
import { imprimirTirada } from '../lib/imprimir';
import { abrir, desplegar, fila, resorte } from '../components/movimiento';
import { altoMinimo, aviso, botonPrimario, botonSecundario, c, campo, chip, etiquetaCampo, mono, tarjeta } from '../theme';

/**
 * Etiquetas de ubicación, para toda la planta y no rack por rack.
 *
 * Planta (`views/planta/Planta.tsx`) sigue imprimiendo un rack entero en 70×30:
 * es lo que hace falta al cargarlo. Esta pantalla es la de reponer: elegir
 * cualquier combinación de racks, un rango de posiciones y niveles dentro de
 * cada uno, en el tamaño de rollo que haya puesto la impresora, y además
 * reimprimir una sola etiqueta rota por su código. No admin-only: la pide
 * cualquiera que esté parado en el depósito con el rollo en la mano.
 */

const PAD2 = (n: number) => String(n).padStart(2, '0');

/** El rango por default al tildar un rack: todo. */
function rangoCompleto(rack: Rack): SeleccionRack {
  return { desde: 1, hasta: rack.columnas, niveles: Array.from({ length: rack.niveles }, (_, i) => rack.niveles - i) };
}

/** Cuántas etiquetas salen de un rack entero, para la tarjeta de elegirlo. */
const etiquetasDelRack = (rack: Rack) => (rack.tipo !== 'rack' ? 1 : rack.columnas * rack.niveles);

/**
 * Valida un código tipeado a mano contra el mapa y arma su etiqueta, o explica
 * por qué no se puede. Nunca pega a la red: todo lo que hace falta ya está en
 * `racks`, que ya se cargó para dibujar las tarjetas.
 */
function etiquetaSuelta(codigoTipeado: string, racks: Rack[]): { etiqueta: EtiquetaUbicacion } | { error: string } {
  const codigo = codigoTipeado.trim().toUpperCase();
  if (!codigo) return { error: 'Escribí un código.' };
  const partes = codigo.split('-');

  if (partes[0] === 'U' && partes.length === 4) {
    const [, id, colTexto, nivelTexto] = partes;
    const rack = racks.find((r) => r.id === id);
    if (!rack) return { error: `No existe el rack ${id}.` };
    if (rack.tipo === 'estacion') return { error: `${id} es una estación: el código es E-${id}-01.` };
    if (rack.tipo === 'piso') return { error: `${id} es una zona de piso: el código es Z-${id}-01.` };
    const col = Number(colTexto);
    const nivel = Number(nivelTexto);
    if (!Number.isInteger(col) || col < 1 || col > rack.columnas)
      return { error: `El rack ${id} tiene posiciones de 01 a ${PAD2(rack.columnas)}.` };
    if (!Number.isInteger(nivel) || nivel < 1 || nivel > rack.niveles)
      return { error: `El rack ${id} tiene niveles de 1 a ${rack.niveles}.` };
    return { etiqueta: etiquetaDePosicion(id!, col, nivel) };
  }

  if (partes[0] === 'E' && partes.length === 3) {
    const [, id] = partes;
    const rack = racks.find((r) => r.id === id);
    if (!rack) return { error: `No existe la estación ${id}.` };
    if (rack.tipo !== 'estacion') return { error: `${id} no es una estación: usá ${rack.tipo === 'piso' ? `Z-${id}-01` : `U-${id}-<posición>-<nivel>`}.` };
    return { etiqueta: codigosDeUbicacion(rack)[0]! };
  }

  if (partes[0] === 'Z' && partes.length === 3) {
    const [, id] = partes;
    const rack = racks.find((r) => r.id === id);
    if (!rack) return { error: `No existe la zona ${id}.` };
    if (rack.tipo !== 'piso') return { error: `${id} no es una zona de piso: usá ${rack.tipo === 'estacion' ? `E-${id}-01` : `U-${id}-<posición>-<nivel>`}.` };
    return { etiqueta: codigosDeUbicacion(rack)[0]! };
  }

  return { error: 'Formato de código inválido. Usá U-<rack>-<posición>-<nivel>, E-<estación>-01 o Z-<zona>-01.' };
}

function TarjetaRack({
  indice,
  rack,
  sel,
  ajustando,
  onAlternar,
  onAjustar,
  onCambiarRango,
  onAlternarNivel,
}: {
  indice: number;
  rack: Rack;
  sel: SeleccionRack | undefined;
  ajustando: boolean;
  onAlternar: () => void;
  onAjustar: () => void;
  onCambiarRango: (campo: 'desde' | 'hasta', valor: number) => void;
  onAlternarNivel: (nivel: number) => void;
}) {
  const elegido = sel !== undefined;
  return (
    <motion.div
      {...fila(indice)}
      // Elegir un rack cambia el color con transición, y la tarjeta se estira sola al abrir el ajuste.
      animate={{ opacity: 1, y: 0, borderColor: elegido ? c.verde : c.borde, backgroundColor: elegido ? c.verdeTinte : c.panel }}
      style={{
        ...tarjeta,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={elegido}
          onChange={onAlternar}
          style={{ marginTop: 3, width: 18, height: 18, flex: '0 0 18px' }}
        />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.texto, fontFamily: mono }}>{rack.id}</span>
            {rack.tipo === 'estacion' && <span style={chip('neu')}>Estación</span>}
            {rack.tipo === 'piso' && <span style={chip('neu')}>Zona de piso</span>}
          </span>
          <span style={{ fontSize: 12.5, color: c.texto2 }}>
            {rack.tipo !== 'rack'
              ? '1 etiqueta'
              : `${rack.columnas} posiciones × ${rack.niveles} niveles = ${etiquetasDelRack(rack)} etiquetas`}
          </span>
        </span>
      </label>

      <AnimatePresence initial={false}>
      {elegido && rack.tipo === 'rack' && (
        <motion.button
          {...desplegar}
          type="button"
          onClick={onAjustar}
          style={{
            ...botonSecundario,
            alignSelf: 'flex-start',
            minHeight: 34,
            padding: '0 12px',
            fontSize: 12.5,
            overflow: 'hidden',
          }}
        >
          <motion.span animate={{ rotate: ajustando ? 180 : 0 }} style={{ display: 'inline-flex' }}>
            <Icono nombre={ajustando ? 'expand_less' : 'tune'} size={16} />
          </motion.span>
          Ajustar
        </motion.button>
      )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
      {elegido && rack.tipo === 'rack' && ajustando && sel && (
        <motion.div {...desplegar}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10, borderTop: `1px solid ${c.borde}` }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...etiquetaCampo, flex: 1 }}>
              Desde
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={rack.columnas}
                value={sel.desde}
                onChange={(e) => onCambiarRango('desde', Math.max(1, Math.min(rack.columnas, Number(e.target.value) || 1)))}
                style={campo}
              />
            </label>
            <label style={{ ...etiquetaCampo, flex: 1 }}>
              Hasta
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={rack.columnas}
                value={sel.hasta}
                onChange={(e) => onCambiarRango('hasta', Math.max(1, Math.min(rack.columnas, Number(e.target.value) || 1)))}
                style={campo}
              />
            </label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: c.texto3 }}>Niveles</span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {Array.from({ length: rack.niveles }, (_, i) => rack.niveles - i).map((nivel) => (
                <label key={nivel} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: c.texto, minHeight: 32 }}>
                  <input type="checkbox" checked={sel.niveles.includes(nivel)} onChange={() => onAlternarNivel(nivel)} />
                  N{nivel}
                </label>
              ))}
            </div>
          </div>
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function EtiquetasUbicacion() {
  const { datos, cargando, error, recargar } = useApi(
    async () => {
      const [mapa, catalogo] = await Promise.all([api.mapa(''), api.catalogoEtiquetas()]);
      return { mapa, catalogo };
    },
    [],
  );

  const [seleccion, setSeleccion] = useState<Record<string, SeleccionRack>>({});
  const [ajustando, setAjustando] = useState<string | null>(null);
  const [sueltas, setSueltas] = useState<EtiquetaUbicacion[]>([]);
  const [textoSuelto, setTextoSuelto] = useState('');
  const [errorSuelto, setErrorSuelto] = useState('');
  const [tamanoId, setTamanoId] = useState('');
  // Null es «como sale del rollo»: cambia sola si se cambia el tamaño.
  const [orientacionElegida, setOrientacion] = useState<Orientacion | null>(null);
  const [problema, setProblema] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Leyendo el mapa del depósito…" />;
  if (!datos) return null;

  const { mapa, catalogo } = datos;
  const sectores: Sector[] = mapa.sectores.filter((s) => s.racks.length > 0);
  const racksTodos = sectores.flatMap((s) => s.racks);

  const medida =
    catalogo.tamanos.find((t) => t.id === tamanoId) ??
    catalogo.tamanos.find((t) => t.id === '100x100') ??
    catalogo.tamanos[0]!;

  const cuadrada = medida.ancho === medida.alto;
  const orientacion = cuadrada ? orientacionNatural(medida) : (orientacionElegida ?? orientacionNatural(medida));
  // La vista previa se ve como se va a leer pegada, no como sale del rollo.
  const area = areaDeDibujo(medida, orientacion);

  const etiquetas = [...etiquetasElegidas(racksTodos, seleccion), ...sueltas];
  const total = etiquetas.length;
  const noEntran = etiquetas.filter((e) => !codigoEntra(e, medida, orientacion)).length;
  const preview = etiquetas.slice(0, 12);

  function alternarRack(rack: Rack) {
    setSeleccion((prev) => {
      const copia = { ...prev };
      if (rack.id in copia) delete copia[rack.id];
      else copia[rack.id] = rangoCompleto(rack);
      return copia;
    });
    setAjustando((act) => (act === rack.id ? null : act));
  }

  function elegirSector(sector: Sector) {
    setSeleccion((prev) => ({ ...prev, ...Object.fromEntries(sector.racks.map((r) => [r.id, rangoCompleto(r)])) }));
  }

  function elegirTodo() {
    setSeleccion(Object.fromEntries(racksTodos.map((r) => [r.id, rangoCompleto(r)])));
  }

  function elegirNada() {
    setSeleccion({});
    setAjustando(null);
  }

  function cambiarRango(rackId: string, campoRango: 'desde' | 'hasta', valor: number) {
    setSeleccion((prev) => {
      const actual = prev[rackId];
      if (!actual) return prev;
      return { ...prev, [rackId]: { ...actual, [campoRango]: valor } };
    });
  }

  function alternarNivel(rackId: string, nivel: number) {
    setSeleccion((prev) => {
      const actual = prev[rackId];
      if (!actual) return prev;
      const niveles = actual.niveles.includes(nivel) ? actual.niveles.filter((n) => n !== nivel) : [...actual.niveles, nivel];
      return { ...prev, [rackId]: { ...actual, niveles } };
    });
  }

  function agregarSuelta() {
    const resultado = etiquetaSuelta(textoSuelto, racksTodos);
    if ('error' in resultado) return setErrorSuelto(resultado.error);
    setSueltas((prev) => [...prev, resultado.etiqueta]);
    setTextoSuelto('');
    setErrorSuelto('');
  }

  function quitarSuelta(codigo: string) {
    setSueltas((prev) => prev.filter((e) => e.codigo !== codigo));
  }

  async function imprimir() {
    if (total === 0) return;
    setProblema('');
    const fallo = await imprimirTirada(
      confirmar,
      { nombre: `Etiquetas de ubicación (${total})`, ancho: medida.ancho, alto: medida.alto },
      etiquetas.map((e) => svgEtiquetaUbicacion(e, medida, orientacion)),
      200,
    );
    if (fallo !== null) setProblema(fallo);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Elegí los racks, el rango de posiciones y niveles, y el tamaño del rollo. Para una sola etiqueta rota, usá «Etiqueta suelta» con su código."
      >
        <button type="button" onClick={elegirTodo} style={{ ...botonSecundario, minHeight: 40, padding: '0 14px' }}>
          Todo
        </button>
        <button type="button" onClick={elegirNada} style={{ ...botonSecundario, minHeight: 40, padding: '0 14px' }}>
          Nada
        </button>
      </Encabezado>

      <AnimatePresence>
        {problema && (
          <motion.div key={problema} role="alert" {...abrir(8)} style={aviso('err')}>
            {problema}
          </motion.div>
        )}
      </AnimatePresence>

      <div id="wms-etiquetas-ubicacion-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,360px)', gap: 20, alignItems: 'start' }}>
        {/* ------------------------------------------------------ selección */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {sectores.map((sector) => (
            <div key={sector.id} style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: c.texto, flex: 1, minWidth: 160 }}>
                  {sector.nombre}
                </span>
                <button
                  type="button"
                  onClick={() => elegirSector(sector)}
                  style={{ ...botonSecundario, minHeight: 34, padding: '0 12px', fontSize: 12.5 }}
                >
                  Elegir todo el sector
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 10 }}>
                {sector.racks.map((rack, i) => (
                  <TarjetaRack
                    key={rack.id}
                    indice={i}
                    rack={rack}
                    sel={seleccion[rack.id]}
                    ajustando={ajustando === rack.id}
                    onAlternar={() => alternarRack(rack)}
                    onAjustar={() => setAjustando((act) => (act === rack.id ? null : rack.id))}
                    onCambiarRango={(campoRango, valor) => cambiarRango(rack.id, campoRango, valor)}
                    onAlternarNivel={(nivel) => alternarNivel(rack.id, nivel)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* ------------------------------------------------- etiqueta suelta */}
          <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Etiqueta suelta</span>
              <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3 }}>
                Para reimprimir una sola, p. ej. porque se rompió. Escribí su código.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={textoSuelto}
                onChange={(e) => {
                  setTextoSuelto(e.target.value);
                  setErrorSuelto('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && agregarSuelta()}
                placeholder="U-A1-04-2"
                style={{ ...campo, flex: '1 1 150px', minWidth: 0, fontFamily: mono }}
              />
              <button type="button" onClick={agregarSuelta} style={{ ...botonSecundario, minHeight: altoMinimo(42), padding: '0 16px' }}>
                <Icono nombre="add" size={18} />
                Agregar
              </button>
            </div>
            <AnimatePresence>
              {errorSuelto && (
                <motion.span key={errorSuelto} {...desplegar} style={{ ...desplegar.style, fontSize: 12.5, color: c.rojo }}>
                  {errorSuelto}
                </motion.span>
              )}
            </AnimatePresence>
            {sueltas.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
                <AnimatePresence initial={false} mode="popLayout">
                {sueltas.map((e, i) => (
                  <motion.div key={e.codigo} layout {...fila(i)} transition={{ ...fila(i).transition, layout: resorte }} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ fontFamily: mono, color: c.texto, flex: 1 }}>{e.codigo}</span>
                    <span style={{ color: c.texto3 }}>{e.texto}</span>
                    <button
                      type="button"
                      onClick={() => quitarSuelta(e.codigo)}
                      title="Quitar"
                      aria-label={`Quitar ${e.codigo}`}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', width: 40, height: 40, flex: '0 0 40px', display: 'grid', placeItems: 'center' }}
                    >
                      <Icono nombre="close" size={16} color={c.texto3} />
                    </button>
                  </motion.div>
                ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* -------------------------------------------------------- vista previa */}
        <div className="min-[981px]:sticky min-[981px]:top-3" style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={etiquetaCampo}>
            Tamaño del rollo
            <select value={medida.id} onChange={(e) => setTamanoId(e.target.value)} style={campo}>
              {catalogo.tamanos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </label>

          <div style={etiquetaCampo}>
            Orientación
            <div style={{ display: 'flex', gap: 8 }}>
              {(['horizontal', 'vertical'] as const).map((o) => {
                const activa = orientacion === o;
                return (
                  <button
                    key={o}
                    type="button"
                    disabled={cuadrada}
                    aria-pressed={activa}
                    onClick={() => setOrientacion(o)}
                    style={{
                      ...(activa ? botonPrimario : botonSecundario),
                      flex: 1,
                      minHeight: 44,
                      justifyContent: 'center',
                      opacity: cuadrada && !activa ? 0.5 : 1,
                    }}
                  >
                    <Icono nombre={o === 'horizontal' ? 'crop_landscape' : 'crop_portrait'} size={19} />
                    {o === 'horizontal' ? 'Horizontal' : 'Vertical'}
                  </button>
                );
              })}
            </div>
            {cuadrada && (
              <span style={{ fontSize: 12, fontWeight: 400, color: c.texto3 }}>Es cuadrada: sale igual de las dos formas.</span>
            )}
          </div>

          <AnimatePresence initial={false}>
            {noEntran > 0 && (
              <motion.div {...abrir(8)} style={{ ...aviso('warn'), fontSize: 13 }}>
                En {medida.nombre} {orientacion} el código de barras no entra
                {noEntran === total ? '' : ` en ${noEntran} de ${total}`}. Probá la otra orientación o un rollo más ancho.
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
            Se van a imprimir <Numero valor={total} desdeCero={false} /> {total === 1 ? 'etiqueta' : 'etiquetas'}
          </div>

          {total === 0 ? (
            <div style={{ fontSize: 12.5, color: c.texto3 }}>Elegí algún rack, o cargá una etiqueta suelta.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(90px,1fr))', gap: 8 }}>
                <AnimatePresence initial={false} mode="popLayout">
                {preview.map((e, i) => (
                  <motion.div
                    key={`${e.codigo}-${i}`}
                    layout
                    {...fila(i)}
                    style={{ border: `1px solid ${c.borde}`, borderRadius: 6, overflow: 'hidden', lineHeight: 0, aspectRatio: `${area.ancho} / ${area.alto}` }}
                    dangerouslySetInnerHTML={{ __html: svgEtiquetaUbicacion(e, { ancho: area.ancho, alto: area.alto }) }}
                  />
                ))}
                </AnimatePresence>
              </div>
              {total > preview.length && (
                <div style={{ fontSize: 12.5, color: c.texto3 }}>y {total - preview.length} más</div>
              )}
            </>
          )}

          <button
            type="button"
            onClick={imprimir}
            disabled={total === 0}
            style={{ ...botonPrimario, minHeight: 44, padding: '0 18px', opacity: total === 0 ? 0.55 : 1, justifyContent: 'center' }}
          >
            <Icono nombre="print" size={19} />
            <span>
              Imprimir <Numero valor={total} desdeCero={false} /> {total === 1 ? 'etiqueta' : 'etiquetas'}
            </span>
          </button>
        </div>
      </div>
      {dialogo}
    </div>
  );
}
